import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import * as Sentry from "@sentry/react";

// Define the type for user session data
export interface UserSessionData {
  id: string;
  name: string;
  email: string;
  emailNotifications: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Define the context type
interface UserSessionContextType {
  userSession: UserSessionData | null;
  setUserSession: (data: UserSessionData | null) => void;
  isAuthenticated: boolean;
  clearUserSession: () => void;
}

// Create the context with default values
const UserSessionContext = createContext<UserSessionContextType>({
  userSession: null,
  setUserSession: () => {},
  isAuthenticated: false,
  clearUserSession: () => {},
});

// Create a provider component
interface UserSessionProviderProps {
  children: ReactNode;
}

export const UserSessionProvider: React.FC<UserSessionProviderProps> = ({
  children,
}) => {
  const [userSession, setUserSession] = useState<UserSessionData | null>(null);

  // Attach the signed-in user to every Sentry event so a report can be traced
  // back to an account.
  useEffect(() => {
    if (userSession) {
      Sentry.setUser({ id: userSession.id, email: userSession.email });
    } else {
      Sentry.setUser(null);
    }
  }, [userSession]);

  // Helper function to clear the user session
  const clearUserSession = () => {
    setUserSession(null);
  };

  // Check if user is authenticated
  const isAuthenticated = userSession !== null && userSession !== undefined;

  return (
    <UserSessionContext.Provider
      value={{
        userSession,
        setUserSession,
        isAuthenticated,
        clearUserSession,
      }}
    >
      {children}
    </UserSessionContext.Provider>
  );
};

// Create a custom hook for using the context
export const useUserSession = () => useContext(UserSessionContext);

export default UserSessionContext;
